import { useCallback, useState } from 'react';

import { supabase } from '@/lib/supabase';

// Scan-to-CAD accepts MESH scans only — triangle-soup formats a 3D scanner /
// photogrammetry tool produces. Deliberately NOT the parts-module set
// (usePartUpload's stl/step/stp): STEP is our OUTPUT, never a scan input, and a
// scan is a mesh, so this feature owns its own allowlist rather than widening
// the shared one.
export const SCAN_MESH_EXTENSIONS = [
  'stl',
  'obj',
  'ply',
  'glb',
  'gltf',
  'fbx',
  'off',
  '3mf',
] as const;

export const SCAN_MESH_ACCEPT = SCAN_MESH_EXTENSIONS.map((e) => `.${e}`).join(
  ',',
);

function extensionOf(filename: string): string {
  return filename.split('.').pop()?.toLowerCase() ?? '';
}

export function isSupportedScanFile(filename: string): boolean {
  return (SCAN_MESH_EXTENSIONS as readonly string[]).includes(
    extensionOf(filename),
  );
}

export interface UploadedScan {
  /** Path in the private `meshes` bucket ({uid}/scan-to-cad/{id}.{ext}). */
  meshRef: string;
  meshName: string;
}

export interface UseScanUpload {
  uploading: boolean;
  /** REAL upload progress, 0–1 (fraction of bytes sent) from XHR
   *  upload.onprogress; reset to 0 at the start of every upload. */
  progress: number;
  error: string | null;
  upload: (file: File) => Promise<UploadedScan | null>;
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as
  | string
  | undefined;

// Max scan size. Real scans are large (dense point clouds → meshes); allow up to
// 250 MB, well above the 100 MB image cap in the chat path.
const MAX_SCAN_BYTES = 250 * 1024 * 1024;

/**
 * Upload a mesh scan to the private `meshes` bucket under a user-scoped,
 * conversation-independent path (`{uid}/scan-to-cad/{id}.{ext}`). Uses
 * XMLHttpRequest (not `supabase.storage.upload()`) so the progress bar is driven
 * by REAL bytes sent — the storage SDK's fetch path emits no progress events.
 * The XHR posts the raw file to the IDENTICAL Storage REST endpoint the SDK
 * uses, with the user's JWT, so RLS ({uid} = auth.uid) is satisfied the same way.
 * (Same mechanism as the parts module's usePartUpload, scoped to mesh input.)
 */
export function useScanUpload(): UseScanUpload {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const upload = useCallback(
    async (file: File): Promise<UploadedScan | null> => {
      setError(null);
      if (!isSupportedScanFile(file.name)) {
        setError(
          `Upload a mesh scan (${SCAN_MESH_EXTENSIONS.map((e) => '.' + e).join(', ')}).`,
        );
        return null;
      }
      if (file.size > MAX_SCAN_BYTES) {
        setError('Scan exceeds the 250 MB limit.');
        return null;
      }
      if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
        setError('Storage is not configured.');
        return null;
      }
      setUploading(true);
      setProgress(0);
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const userId = session?.user?.id;
        const accessToken = session?.access_token;
        if (!userId || !accessToken) {
          setError('Sign in to upload a scan.');
          return null;
        }
        const ext = extensionOf(file.name);
        const id = crypto.randomUUID();
        const meshRef = `${userId}/scan-to-cad/${id}.${ext}`;

        await new Promise<void>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open(
            'POST',
            `${SUPABASE_URL}/storage/v1/object/meshes/${meshRef}`,
            true,
          );
          xhr.setRequestHeader('authorization', `Bearer ${accessToken}`);
          xhr.setRequestHeader('apikey', SUPABASE_ANON_KEY as string);
          xhr.setRequestHeader('x-upsert', 'false');
          xhr.setRequestHeader('cache-control', 'max-age=3600');
          xhr.setRequestHeader(
            'content-type',
            file.type || 'application/octet-stream',
          );
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) setProgress(e.loaded / e.total);
          };
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              setProgress(1);
              resolve();
            } else {
              let message = `Upload failed (${xhr.status}).`;
              try {
                const body = JSON.parse(xhr.responseText);
                message = body.message || body.error || message;
              } catch {
                /* non-JSON error body — keep the status message */
              }
              reject(new Error(message));
            }
          };
          xhr.onerror = () => reject(new Error('Network error during upload.'));
          xhr.onabort = () => reject(new Error('Upload cancelled.'));
          xhr.send(file);
        });

        return { meshRef, meshName: file.name };
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Upload failed.');
        return null;
      } finally {
        setUploading(false);
      }
    },
    [],
  );

  return { uploading, progress, error, upload };
}
